import React, { useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import AppButton from "../Common/AppButton";
import ActivityLog from "./ActivityLogs/ActivityLog";
import RfiStagesActivityLog from "./ActivityLogs/RfiStagesActivityLog";
import { useNavigate } from "react-router-dom";
import {
  actionType,
  dimensionalStage,
  documentStatus,
  userRoles,
} from "../../utils/constant";
import { formatText, hasPermission } from "../../utils/utilityFunctions";
import theme from "../../utils/theme";
import toast from "react-hot-toast";
import ReportDownloadManager from "./Reports/ReportDownloadManager";
import useGetDataForReportPdfStage from "../../hooks/useGetDataForReportPdfStage";

const HeaderContainer = styled(Paper)(({ theme }) => ({
  position: "fixed",
  top: "76px",
  width: "fill-available",
  zIndex: 1000,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.primary,
  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.08)",
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "64px",
}));

const RfiHeader = ({ rfiDetails = {}, stagesDetails, role, actions }) => {
  const navigate = useNavigate();

  const { basicDetails, basicReportData, componentData, getAllDetails } =
    useGetDataForReportPdfStage({
      rfiDetails,
      stagesDetails,
      stageNumber: 1,
    });

  // State for activity log modal
  const [activityLogOpen, setActivityLogOpen] = useState(false);

  const openActivityLogFcn = () => {
    if (!hasPermission(actions, actionType.activity_log_view)) {
      toast.error("You do not have permission to view activity log");
      return;
    }
    setActivityLogOpen(true);
  };

  const viewDocumentsFcn = () => {
    if (!hasPermission(actions, actionType.documents_view)) {
      toast.error("You do not have permission to view documents");
      return;
    }
    navigate(`/rfi_document/${rfiDetails?.rfi_id}`);
  };

  // Extract needed details from rfiDetails prop
  const { rfi_id, dimensional_stage, status, purpose, documents_status } =
    rfiDetails || {};

  return (
    <HeaderContainer>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mr: 2 }}>
          RFI NO : {rfi_id || "N/A"}
        </Typography>

        {purpose?.purpose_name === "Dimensional" &&
          (status !== "Closed" ? (
            <Typography variant="h6" sx={{ fontWeight: "bold", mr: 3 }}>
              Stage:{" "}
              {dimensionalStage[dimensional_stage.toLowerCase()] ||
                "Stage not available"}
            </Typography>
          ) : (
            <Typography variant="h6" sx={{ fontWeight: "bold", mr: 3 }}>
              Stage :{" "}
              <b
                style={{
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                }}
              >
                Completed
              </b>
            </Typography>
          ))}

        <Box sx={{ display: "flex", alignItems: "center", mr: 3, flex: 1 }}>
          <Typography variant="subtitle2">
            Status:{" "}
            <span
              style={{
                fontWeight: "bold",
                color:
                  status === "Completed"
                    ? theme.palette.success.main
                    : theme.palette.info.main,
              }}
            >
              {(status && formatText(status)) || "In Progress"}
            </span>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <AppButton
            variant="contained"
            buttonType="main"
            size="small"
            startIcon={<DescriptionIcon />}
            label={
              (documents_status === documentStatus.Awaiting_Documents ||
                documents_status === documentStatus.Partially_Uploaded) &&
              role === userRoles.Document_Controller
                ? "Upload Documents"
                : documents_status === documentStatus.Awaiting_E_Signature &&
                  role === userRoles.Quality_Controller_Coordinator
                ? "Sign Documents"
                : "View Documents"
            }
            onClick={() => viewDocumentsFcn()}
            sx={{
              minWidth: "auto",
            }}
          />

          {/* Report Download Manager - Handles all report downloads and preview */}
          {purpose?.purpose_name === "Dimensional" && (
            <ReportDownloadManager
              stagesDetails={stagesDetails}
              rfiDetails={rfiDetails}
              getAllDetails={getAllDetails}
              basicDetails={basicDetails}
              basicReportData={basicReportData}
              componentData={componentData}
            />
          )}

          <AppButton
            variant="contained"
            buttonType="secondary"
            size="small"
            startIcon={<HistoryIcon />}
            label="Activity Log"
            onClick={() => openActivityLogFcn()}
            sx={{
              minWidth: "auto",
            }}
          />
          {/* Activity Log Modal - Different components for dimensional vs non-dimensional */}
          {rfiDetails?.purpose?.purpose_name?.toLowerCase() ===
          "dimensional" ? (
            <RfiStagesActivityLog
              open={activityLogOpen}
              onClose={() => setActivityLogOpen(false)}
              rfiNo={rfi_id}
            />
          ) : (
            <ActivityLog
              open={activityLogOpen}
              onClose={() => setActivityLogOpen(false)}
              rfiNo={rfi_id}
              type={
                rfiDetails?.purpose?.purpose_name?.toLowerCase() ||
                "non-dimensional"
              }
            />
          )}
        </Box>
      </Box>
    </HeaderContainer>
  );
};

export default RfiHeader;
